import React, { ReactNode } from 'react';
import {
  TripDataProvider,
  useTripData,
  TripDataContextType
} from './trip-data-context';
import {
  TripSelectionProvider,
  useTripSelection,
  TripSelectionContextType
} from './trip-selection-context';
import {
  TripInteractionProvider,
  useTripInteraction,
  TripInteractionContextType
} from './trip-interaction-context';
import {
  TripComputedFoldersProvider,
  useTripComputedFolders,
  TripComputedFoldersContextType
} from './trip-computed-folders-context';

/**
 * Composite type that combines all trip-related contexts
 * Used for backward compatibility with the old monolithic useTrip() hook
 */
export type TripContextType = TripDataContextType &
  TripSelectionContextType &
  TripInteractionContextType &
  TripComputedFoldersContextType;

/**
 * Composite Provider that wraps all trip-related contexts
 * This maintains the same external API while internally using separate contexts
 */
export const TripProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  return (
    <TripDataProvider>
      <TripSelectionProvider>
        <TripInteractionProvider>
          <TripComputedFoldersProvider>{children}</TripComputedFoldersProvider>
        </TripInteractionProvider>
      </TripSelectionProvider>
    </TripDataProvider>
  );
};

/**
 * Legacy hook for backward compatibility
 * Combines all contexts into a single object
 *
 * ⚠️ WARNING: This hook causes components to re-render when ANY context changes
 * Prefer using specialized hooks instead:
 * - useTripData() - for core trip data
 * - useTripSelection() - for folder/destination/activity selection
 * - useTripInteraction() - for hover and distance calculations
 * - useTripComputedFolders() - for computed folders feature
 */
export const useTrip = (): TripContextType => {
  const data = useTripData();
  const selection = useTripSelection();
  const interaction = useTripInteraction();
  const computedFolders = useTripComputedFolders();

  return {
    ...data,
    ...selection,
    ...interaction,
    ...computedFolders
  };
};

// Re-export specialized hooks for convenience
export {
  useTripData,
  useTripSelection,
  useTripInteraction,
  useTripComputedFolders
};
